import {
  UnitTestLoaderParameters,
  UnitTestSchema,
  WithRunnerData,
} from '../UnitTestSchema';
import {
  EMPTY_TEST_RESULT,
  getFirstTestFailed,
  UnitTestResults,
} from '../unitTestResults';
import {
  getConsoleUnitTestReference,
  getWhitespaceModeForConsoleUnitTests,
  selectUnitTestsForMilestone,
} from '../../models/unitTests/console';
import {getDoc} from 'firebase/firestore';
import {
  areConsoleOutputsEqual,
  getCasingModeFromLegacyFlag,
  getTranscriptInputs,
  getTranscriptOutputs,
  ObservedConsoleTranscript,
} from '@cip/code-runner';
import {ConsoleEditor} from './ConsoleEditor';
import {type LoadedConsoleUnitTestData} from './types';
import {constructFilesListWithContent, getAllFileNames} from 'ide/utils/general';
import type {TranslationFunction} from 'components/translation/utils';

async function loader({
  courseId,
  assignmentId,
}: UnitTestLoaderParameters): Promise<LoadedConsoleUnitTestData | null> {
  const consoleUnitTestReference = getConsoleUnitTestReference(
    courseId,
    assignmentId,
  );
  const consoleUnitTestDoc = await getDoc(consoleUnitTestReference);
  if (!consoleUnitTestDoc.exists()) {
    return null;
  }
  const unitTestsDocument = consoleUnitTestDoc.data();
  return {
    unitTestData: unitTestsDocument,
  };
}

async function runner({
  loadedData,
  pyodideClient,
  filesCode,
  fileStructure,
  selectedMilestoneId,
}: WithRunnerData<LoadedConsoleUnitTestData>): Promise<UnitTestResults> {
  const {unitTestData} = loadedData ?? {unitTestData: {unitTests: []}};
  if (
    !unitTestData ||
    !unitTestData.unitTests ||
    unitTestData.unitTests.length === 0
  ) {
    return EMPTY_TEST_RESULT;
  }
  const unitTests = selectUnitTestsForMilestone({
    unitTests: unitTestData.unitTests ?? [],
    selectedMilestoneId,
  });
  const whitespaceMode = getWhitespaceModeForConsoleUnitTests(
    unitTestData.options,
  );
  const shouldIgnoreCasingInAutograder =
    unitTestData.options?.shouldIgnoreCasingInAutograder ?? false;
  // A milestone with no cases of its own lands here. `EMPTY_TEST_RESULT` has
  // `testsRun: false`, so nothing is banked for a check that never ran.
  if (unitTests.length === 0) {
    return EMPTY_TEST_RESULT;
  }
  const allFileNames = getAllFileNames(fileStructure);
  const mainFile = allFileNames.find(file => file.name === 'main.py');
  if (!mainFile) {
    return {
      testResults: [
        {
          isSuccess: false,
          name: 'Console Project Precheck',
          usedAi: false,
          rawResult: {message: 'No main.py file found'},
        },
      ],
      testsRun: true,
    };
  }
  const codeToRun = filesCode[mainFile.id]?.content ?? '';
  const testResults = [];
  for (let i = 0; i < unitTests.length; i++) {
    const unitTest = unitTests[i];
    const expectedInputs = unitTest.transcript
      .filter(entry => entry.type === 'input')
      .map(entry => entry.input);
    const expectedOutputs = unitTest.transcript.map(entry => entry.output);
    if (!expectedInputs) {
      console.error('unit test expected inputs are empty');
      continue;
    }
    let inputPtr = 0;
    const observedTranscript = new ObservedConsoleTranscript();
    const rawResult = await pyodideClient.runCode(codeToRun, {
      files: constructFilesListWithContent(fileStructure, filesCode),
      stepMode: true,
      testMode: 'simple',
      randomSeed: unitTest.randomSeed,
      ioOverrides: {
        input: async () => {
          if (inputPtr >= expectedInputs.length) {
            return '';
          }
          const inputValue = expectedInputs[inputPtr] ?? '';
          inputPtr += 1;
          observedTranscript.recordInput(inputValue);
          return inputValue;
        },
        output: (output: string) => {
          observedTranscript.appendOutput(output);
        },
        error: () => {},
        onRunEnd: () => {
          observedTranscript.finish();
        },
      },
    });
    const observedConsoleTranscript = observedTranscript.getEntries();
    const observedOutputs = getTranscriptOutputs(observedConsoleTranscript);
    const correctOutput = areConsoleOutputsEqual(
      expectedOutputs,
      observedOutputs,
      {
        whitespaceMode,
        casingMode: getCasingModeFromLegacyFlag(shouldIgnoreCasingInAutograder),
      },
    );
    const hasError = rawResult.error && rawResult.error.length > 0;
    const isSuccess = !hasError && correctOutput;
    testResults.push({
      isSuccess,
      name: unitTest.name,
      usedAi: false,
      rawResult: {
        ...rawResult,
        output: observedOutputs,
        input: getTranscriptInputs(observedConsoleTranscript),
      },
    });
  }
  return {testResults, testsRun: true};
}

function getFailureMessage(
  results: UnitTestResults,
  t: TranslationFunction,
): string {
  const firstTestFailed = getFirstTestFailed(results);
  return (
    t(
      "The program finished running but the output wasn't what we expected. Check Test ",
    ) + firstTestFailed
  );
}

export const CONSOLE_UNIT_TEST_SCHEMA: UnitTestSchema<LoadedConsoleUnitTestData> =
  {
    loader,
    runner,
    EditorComponent: ConsoleEditor,
    unitTestType: 'console',
    displayName: 'Console Input/Output Script',
    tooltipDescription:
      'Compares a transcript of input/output pairs against the student code.',
    getFailureMessage,
  };
